/**
 * Backlog #52: the in-app AI assistant that can carry out actions through chat (Gemini, with a scripted mock).
 * Shared between the API (tool registry, orchestrator) and the web panel (streaming events, preview cards).
 */

export type AiProviderName = 'gemini' | 'mock';
export type AiProviderChoice = AiProviderName | 'off';

/** Models offered in Settings > AI. Any Gemini model id can be typed in; these are the suggestions. */
export const GEMINI_MODELS: { id: string; label: string; tier: 'flash' | 'pro' }[] = [
  { id: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash (recommended, lower cost)', tier: 'flash' },
  { id: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro (more capable, about 3x the cost)', tier: 'pro' },
];
export const DEFAULT_GEMINI_MODEL = 'gemini-3.8-flash';

/**
 * Estimated price in pence per million tokens, used for the monthly budget cap. Converted from Google's USD list
 * prices at $1 = 75p, using the higher 2027 Flash rates so the cap errs on the safe side. Unknown models are
 * priced as Pro.
 */
export const AI_PRICE_PENCE_PER_MTOK: Record<string, { input: number; output: number }> = {
  'gemini-3.8-flash': { input: 113, output: 563 },
  'gemini-3.6-flash': { input: 113, output: 563 },
  'gemini-3.5-flash-lite': { input: 23, output: 188 },
  'gemini-3.1-pro-preview': { input: 150, output: 900 },
  mock: { input: 0, output: 0 },
};
export function estimateCostPence(model: string, inputTokens: number, outputTokens: number): number {
  const p = AI_PRICE_PENCE_PER_MTOK[model] ?? (model.startsWith('mock') ? AI_PRICE_PENCE_PER_MTOK.mock : AI_PRICE_PENCE_PER_MTOK['gemini-3.1-pro-preview']);
  return Math.round(((inputTokens * p.input + outputTokens * p.output) / 1_000_000) * 10_000) / 10_000;
}

export interface AiSettingsView {
  /** What an admin chose; null = automatic (Gemini when a key is set, otherwise demo mode). See `effective`. */
  provider: AiProviderChoice | null;
  /** Where the key in use comes from. */
  key_source: 'settings' | 'env' | 'none';
  key_last4: string | null;
  model: string;
  enabled: boolean;
  monthly_budget_pence: number;
  user_requests_per_hour: number;
  workspace_requests_per_hour: number;
  retention_days: number;
  can_store_secrets: boolean;
  effective: { provider: AiProviderChoice; model: string; reason: string };
  month: { cost_pence: number; requests: number; input_tokens: number; output_tokens: number };
  updated_at: string | null; updated_by_name: string | null;
}
export interface AiSettingsInput {
  provider?: AiProviderChoice | null;
  model?: string | null;
  enabled?: boolean;
  monthly_budget_pence?: number;
  user_requests_per_hour?: number;
  workspace_requests_per_hour?: number;
  retention_days?: number;
  api_key?: string;
  clear_api_key?: boolean;
}
export interface AiUsageEntry {
  id: string; created_at: string; user_id: string | null; display_name: string | null;
  /** t445u: summaries, digests, meeting notes, transcription and translation through the saved Gemini key count too. */
  kind: 'chat' | 'test' | 'catch_up' | 'digest' | 'meeting_notes' | 'transcribe' | 'translate';
  provider: string; model: string; status: 'ok' | 'error' | 'rate_limited' | 'budget' | 'off';
  input_tokens: number; output_tokens: number; cost_pence: number; tool_calls: number; duration_ms: number; error: string | null;
}

/** What the page the user is on tells the assistant (e.g. the Schedule's week, building and filters). */
export interface AssistantPageContext {
  path: string;
  title?: string | null;
  schedule?: { week_start: string; location_id: string | null; location_name?: string | null; filters?: Record<string, string | string[] | null> } | null;
}

export interface AssistantCitation { n: number; title: string; link: string; kind: 'guide' | 'company' | 'kb' | 'source' | 'schedule' | 'leave' | 'record'; snippet?: string }

export type AssistantCardField = { label: string; value: string; before?: string | null };
export interface RotaGridCell { date: string; shifts: { start: string; end: string; position: string | null; is_new: boolean }[] }
export interface RotaGrid {
  location_name: string; week_start: string; days: string[];
  rows: { user_id: string | null; name: string; cells: RotaGridCell[]; hours: number }[];
  gaps: { date: string; start: string; end: string; position: string | null; reason: string }[];
}
export interface AssistantPreview {
  kind: 'rota' | 'vehicle' | 'journey' | 'task' | 'todo' | 'shift_message' | 'shifts' | 'shift' | 'issue' | 'shift_offer' | 'shift_edit' | 'leave' | 'kudos' | 'message';
  title: string;
  summary: string;
  fields?: AssistantCardField[];
  grid?: RotaGrid;
  warnings?: string[];
  notes?: string[];
  /** Shift messaging: the compose dialog is prefilled from here; nothing is sent from the chat. */
  compose?: { shift_ids: string[]; body: string; mode: 'group_dm' | 'channel' | 'announcement'; label: string };
}
export type AssistantActionStatus = 'proposed' | 'applied' | 'cancelled' | 'undone' | 'failed' | 'expired';
export interface AssistantAction {
  id: string; tool: string; status: AssistantActionStatus; preview: AssistantPreview; can_undo: boolean;
  result?: { message: string; link?: string | null } | null; error?: string | null; created_at: string; expires_at: string;
}

export interface AssistantChatMessage {
  id: string; role: 'user' | 'assistant'; text: string; created_at: string;
  citations?: AssistantCitation[]; actions?: AssistantAction[]; tools?: string[];
}
export interface AssistantConversationSummary { id: string; title: string; updated_at: string; created_at: string }

/** Server-sent events from POST /assistant/chat. */
export type AssistantStreamEvent =
  | { type: 'start'; conversation_id: string; message_id: string }
  | { type: 'text'; delta: string }
  | { type: 'tool'; name: string; label: string }
  | { type: 'action'; action: AssistantAction }
  | { type: 'citations'; citations: AssistantCitation[] }
  | { type: 'done'; message: AssistantChatMessage }
  | { type: 'error'; message: string; code?: string };

export interface AssistantChatStatus {
  available: boolean; provider: AiProviderChoice; model: string | null; demo: boolean; reason: string | null;
  used_this_hour: number; per_hour: number; retention_days: number;
}

/** Splits an answer into text and [n] citation markers (only numbers that exist). */
export function citationSegments(text: string, count: number): ({ kind: 'text'; text: string } | { kind: 'cite'; n: number })[] {
  const out: ({ kind: 'text'; text: string } | { kind: 'cite'; n: number })[] = [];
  let last = 0;
  for (const m of text.matchAll(/\[(\d{1,2})\]/g)) {
    const n = Number(m[1]);
    if (n < 1 || n > count) continue;
    if (m.index! > last) out.push({ kind: 'text', text: text.slice(last, m.index) });
    out.push({ kind: 'cite', n });
    last = m.index! + m[0].length;
  }
  if (last < text.length) out.push({ kind: 'text', text: text.slice(last) });
  return out;
}

/** Wraps user-written content before it goes to the model: it is data, never instructions. */
export function asData(label: string, value: unknown): string {
  const s = typeof value === 'string' ? value : JSON.stringify(value);
  return `<<<${label}\n${s.replace(/<<<|>>>/g, '‹‹‹')}\n>>>`;
}
